import React from 'react';
import { Button, Grid } from '@mui/material';
import { ensureInternalURLTrailingSlash } from '@helpers/string';
import { useAtom } from 'jotai';
import { selectedLanguageAtom } from '@atoms/appSettings';
import { useTranslation } from '@stateManagement/TranslationContext';
import { getTranslation } from '@helpers/languageTranslations';
import { getLanguageRouteAndPrefixByCodeAndRouteKey } from '@helpers/languages';
import { languageRouteTypes } from '@helpers/languageRoutes';
import { colors, getRgbaColor, breakpoints } from '@web-for-marketing/react-ui';
import { Heading } from '@web-for-marketing/react-ui';

const classes = {
    tagButton: {
        margin: '1rem 1rem 1rem 0',
        color: colors.core.innovation,
        backgroundColor: '#ffffff',
        border: `1px solid ${colors.core.innovation}`,
        borderRadius: '2.5rem',
        minHeight: 'unset',
        padding: '.5rem 2.5rem !important',
        fontWeight: '500',
        fontSize: '1.8rem',
        width: 'auto',
        transition: 'background-color 400ms ease, font-weight 400ms ease',
        '&:hover': {
            backgroundColor: getRgbaColor(colors.core.deepSpace, 0.05),
            fontWeight: '700',
        },
        [`@media (max-width: ${breakpoints.md})`]: {
            width: 'auto',
            fontSize: '1.6rem',
        },
    },
    tagContainer: {
        marginTop: '1rem',
    },
};

export interface RenderChipsProps {
    chips: {
        name: string;
        slug: string;
        hidden: boolean;
    }[];
    hideHeading?: boolean;
    overrideHeadingText?: string;
}

export function RenderChips({
    chips,
    overrideHeadingText,
    hideHeading = false,
}: RenderChipsProps): React.ReactElement | null {
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const { selectedLanguageTranslation } = useTranslation();
    const translatedRenderChips = getTranslation(selectedLanguageTranslation, 'renderChips');
    const nonHiddenChips = chips.filter((chip) => !chip.hidden);

    return nonHiddenChips.length ? (
        <>
            {!hideHeading ? (
                <Heading variant='h4' component='h2'>
                    {overrideHeadingText ? overrideHeadingText : translatedRenderChips.postTags}
                </Heading>
            ) : null}
            <Grid container css={classes.tagContainer}>
                {nonHiddenChips.map((chip) => (
                    <Button
                        key={chip.name}
                        aria-label={`${chip.name} ${translatedRenderChips.tag}`}
                        href={ensureInternalURLTrailingSlash(
                            `${getLanguageRouteAndPrefixByCodeAndRouteKey(selectedLanguage, languageRouteTypes.TAG)}${
                                chip.slug
                            }`
                        )}
                        variant='contained'
                        color='primary'
                        css={classes.tagButton}
                        component='a'
                    >
                        {chip.name}
                    </Button>
                ))}
            </Grid>
        </>
    ) : null;
}
